<template>
    <v-container class="pa-5" fluid>
        <AssignUserDialog
            :accident="accident"
            :is-open="assignUserDialog"
            :loading="assignLoading"
            @save-assign-user="updateAccident"
            @close-assign-user-dialog="closeAssignUserDialog"
        />
        <AssignStatusDialog
            :accident="accident"
            :is-open="assignStatusDialog"
            :can-update-declared-fixed-time="canUpdateDeclaredFixedTime"
            :loading="assignLoading"
            @save-assign-status="updateAccident"
            @close-assign-status-dialog="closeAssignStatusDialog"
        />
        <AssignDepartmentDialog
            :accident="accident"
            :is-open="assignDepartmentDialog"
            :loading="assignLoading"
            @save-assign-department="updateAccident"
            @close-assign-department-dialog="closeAssignDepartmentDialog"
        />
        <DeclaredFixTimeDialog
            :is-open="declaredFixTimeDialog"
            :accident-data="accident"
            :loading="declaredFixTimeLoading"
            @on-save="saveDeclaredFixTime"
            @on-abort="closeDeclaredFixTimeDialog"
        />

        <CodeReaderDialog
            :is-open="codeReaderDialog"
            :settings="codeReaderSettings"
            @code-stream-start="showCodeReaderDialog"
            @code-stream-stop="hideCodeReaderDialog"
            @code-stream-handle="handleQrJSON"
        />

        <page-title>{{ pageTitle }}</page-title>
        <v-row align="center">
            <v-col class="pb-0">
                <v-btn
                    text
                    color="primary"
                    class="pa-0"
                    @click="close"
                >
                    <v-icon color="primary">
                        mdi-arrow-left
                    </v-icon>
                    Powrót do listy
                </v-btn>
            </v-col>
        </v-row>
        <v-row tag="form">
            <v-container>
                <v-row>
                    <v-col cols="12" xl="8" offset-xl="2">
                        <h2 class="text-h6">
                            Dane awarii
                        </h2>
                    </v-col>
                    <v-col cols="12" xl="8" offset-xl="2">
                        <v-card>
                            <v-card-text>
                                <v-text-field
                                    v-model="accident.accidentType.name"
                                    readonly
                                    label="Typ awarii"
                                    item-value="name"
                                />
                                <v-text-field
                                    v-model="accidentStatus"
                                    readonly
                                    label="Status"
                                    item-value="name"
                                />
                                <v-text-field
                                    v-model="accident.machine.name"
                                    readonly
                                    label="Maszyna"
                                    item-value="name"
                                />
                                <v-text-field
                                    v-model="accident.accidentDepartment.name"
                                    readonly
                                    label="Dział"
                                    item-value="name"
                                />
                                <v-textarea
                                    v-model="accident.description"
                                    readonly
                                    name="Opis"
                                    label="Opis"
                                    item-value="name"
                                />
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" xl="8" offset-xl="2">
                        <v-btn
                            :disabled="isAccidentClosed || isLoggedUserAssigned || !isAccidentAccepted"
                            class="ma-1"
                            data-sentry-label="Przypisz do mnie"
                            @click="showAssignUserDialog"
                        >
                            Przypisz do mnie
                        </v-btn>
                        <v-btn
                            :disabled="(isAccidentClosed || !isLoggedUserAssigned) && !hasAccidentChangeDeletedStatus"
                            class="ma-1"
                            data-sentry-label="Zmień Status"
                            @click="showAssignStatusDialog"
                        >
                            Zmień Status
                        </v-btn>
                        <v-btn
                            :disabled="isAccidentClosed || !isLoggedUserAssigned || !isAccidentAccepted"
                            class="ma-1"
                            data-sentry-label="Przekieruj"
                            @click="showAssignDepartmentDialog"
                        >
                            Przekieruj
                        </v-btn>
                        <v-btn
                            v-if="isAdminUser"
                            :disabled="isAccidentClosed || isAccidentAccepted"
                            class="ma-1"
                            color="success"
                            data-sentry-label="Zatwierdź"
                            @click="acceptAccident"
                        >
                            Zatwierdź
                        </v-btn>
                        <v-btn
                            v-if="(isAdminUser || canChangeStatusForDuringRemoval) && !isAccidentClosed && !isAccidentInProgress && isAssigned"
                            color="primary"
                            class="ma-1"
                            data-sentry-label="Rozpocznij usuwanie"
                            @click="startFixAccident"
                        >
                            Rozpocznij usuwanie
                        </v-btn>
                        <v-btn
                            v-if="canSetSafeMode && !isAccidentClosed"
                            color="warning"
                            class="ma-1"
                            data-sentry-label="Tryb awaryjny"
                            :disabled="isSafeMode"
                            :loading="actionLoading"
                            @click="setSafeMode"
                        >
                            Tryb awaryjny
                        </v-btn>
                        <v-btn
                            v-if="canUpdateDeclaredFixedTime && isLoggedUserAssigned && !isAccidentStatusExcluded && !isAccidentClosed"
                            class="ma-1"
                            data-sentry-label="Przekieruj"
                            @click="showDeclaredFixTimeDialog"
                        >
                            Czas usunięcia awarii
                        </v-btn>
                        <v-btn
                            v-if="!(isAdminUser || canChangeStatusForDuringRemoval) && hasCamera && !isAccidentClosed && !isAccidentInProgress"
                            :disabled="!isLoggedUserAssigned"
                            color="primary"
                            class="elevation-1 mx-1"
                            data-sentry-label="Otwórz czytnik kodów"
                            @click="showCodeReaderDialog({
                                expectedQrTypeCodes: ['ZA'],
                                origin: null
                            })"
                        >
                            <v-icon>mdi-camera</v-icon>
                        </v-btn>
                    </v-col>
                </v-row>
            </v-container>
        </v-row>
        <v-row>
            <v-container>
                <v-col cols="12" xl="8" offset-xl="2">
                    <h2 class="text-h6">
                        Komentarze dla awarii
                    </h2>
                </v-col>
                <v-col cols="12" xl="8" offset-xl="2">
                    <v-card>
                        <v-list v-if="hasComments" two-line>
                            <template v-for="(comment, index) in accident.comment">
                                <v-list-item
                                    :key="comment.createdAt"
                                >
                                    <v-list-item-avatar>
                                        <v-avatar
                                            size="40px"
                                            color="blue-grey"
                                        >
                                            <span class="white--text text-uppercase">
                                                {{ parseUserInitials(comment.createdBy.username) }}
                                            </span>
                                        </v-avatar>
                                    </v-list-item-avatar>
                                    <v-list-item-content>
                                        <v-list-item-title>
                                            {{ comment.comment }}
                                        </v-list-item-title>
                                        <v-list-item-subtitle>
                                            {{ comment.createdBy.username }} - {{ formatDate(comment.createdAt) }}
                                        </v-list-item-subtitle>
                                    </v-list-item-content>
                                </v-list-item>
                                <v-divider :key="index" />
                            </template>
                        </v-list>
                        <v-card-text v-else>
                            <v-alert
                                type="info"
                                text
                                border="left"
                            >
                                Brak komentarzy dla awarii.
                            </v-alert>
                        </v-card-text>

                        <v-card-text>
                            <v-textarea
                                v-model="newComment"
                                label="Dodaj komentarz"
                                :disabled="actionLoading"
                                auto-grow
                                clearable
                                hide-details
                            />
                            <v-row justify="end">
                                <v-col>
                                    <v-btn
                                        color="primary"
                                        :disabled="newComment === null || newComment.length <= 0"
                                        :loading="actionLoading"
                                        data-sentry-label="Wyślij komentarz"
                                        @click="addComment"
                                    >
                                        Wyślij
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-container>
        </v-row>
    </v-container>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import store from '@/./store'
import { errorHandler } from '@/utils/errorHandler'
import AssignUserDialog from './AssignUserDialog'
import AssignStatusDialog from './AssignStatusDialog'
import AssignDepartmentDialog from './AssignDepartmentDialog'
import DeclaredFixTimeDialog from './DeclaredFixTimeDialog'
import CodeReaderDialog from '@/components/common/CodeReaderDialog'
import dateFormatter from '@/mixins/dateFormatter'
import parse from '@/mixins/parse'
import { hasUserPermission } from '@/utils/permissionsHandler'
import decodeQRcode from '@/mixins/decodeQRcode'

export default {
    name: 'Accident',
    components: {
        PageTitle,
        AssignUserDialog,
        AssignStatusDialog,
        AssignDepartmentDialog,
        CodeReaderDialog,
        DeclaredFixTimeDialog
    },
    mixins: [
        dateFormatter,
        decodeQRcode,
        parse
    ],
    beforeRouteEnter(to, from, next) {
        Promise.all([
            store.dispatch('maintenance/getAccident', to.params.id),
            store.dispatch('administration/getUser', store.state.user.userId),
        ])
            .then(responses => {
                next(vm => {
                    vm.accident = Object.assign({}, responses[0].data)
                    vm.pageTitle = `${to.meta.title} / ${vm.accident.id}`
                    vm.userData = responses[1].data
                })
            })
            .catch(error => {
                next(error)
                errorHandler(error)
            })
    },
    data() {
        return {
            pageTitle: '',
            userData: {},
            accident: {
                accidentType: {
                    name: null
                },
                accidentStatus: {
                    name: null
                },
                machine: {
                    name: null
                },
                accidentDepartment: {
                    name: null
                },
                description: null,
                lastAssignee: null,
                declaredFixTime: null
            },
            assignUserDialog: false,
            assignStatusDialog: false,
            assignDepartmentDialog: false,
            assignLoading: false,
            actionLoading: false,
            newComment: '',
            codeReaderSettings: {
                expectedQrTypeCodes: null,
                origin: null
            },
            codeReaderDialog: false,
            declaredFixTimeDialog: false,
            declaredFixTimeLoading: false,
            declaredFixTime: null,
            excludeStatuses: [10, 30, 100] //['Przypisana', 'Przekierowana', 'Usunięta']
        }
    },
    computed: {
        user() {
            return this.$store.state.user
        },
        isAdminUser() {
            return hasUserPermission('accidents_admin')
        },
        canChangeStatusForDuringRemoval() {
            return hasUserPermission('accident_change_status_for_during_removal')
        },
        canSetSafeMode() {
            return hasUserPermission('accident_safe_mode_update')
        },
        isLoggedUserAssigned() {
            if (this.accident.lastAssignee) {
                return this.accident.lastAssignee.id == this.user.userId
            }

            return false
        },
        isAccidentClosed() {
            return this.accident.accidentStatusId == 100
        },
        isAccidentInProgress() {
            return this.accident.accidentStatusId == 20
        },
        isSafeMode() {
            return this.accident.accidentTypeId == 2 
        },
        isAccidentStatusExcluded() {
            return this.excludeStatuses.includes(this.accident.accidentStatusId)
        },
        isAccidentAccepted() {
            return this.accident.adminAccepted
        },
        isAssigned() {
            return this.accident.lastAssignee !== null
        },
        hasCamera() {
            return this.$store.state.common.hasCamera
        },
        hasComments() {
            return this.accident.comment ? this.accident.comment.length > 0 : false
        },
        userName() {
            return this.$store.state.user.userName
        },
        accidentStatus() {
            if (this.accident.lastAssignee) {
                return `${this.accident.accidentStatus.name} | ${this.accident.lastAssignee.firstName} ${this.accident.lastAssignee.lastName}`
            }

            return this.accident.accidentStatus.name
        },
        userId() {
            return this.$store.state.user.userId
        },
        hasAccidentChangeDeletedStatus() {
            return hasUserPermission('accident_change_deleted_status')
        },
        canUpdateDeclaredFixedTime() {
            return this.userData?.profile?.accidentDepartmentsIds.some(item => item === 1) && this.accident.accidentDepartmentId === 1
        }


    },
    methods: {
        updateAccident(payload) {
            this.assignLoading = true

            this.accident.username = this.user.userName
            this.accident.accidentStatusId = payload.accidentStatusId
            !payload.comment ? this.accident.accidentStatusHistory.comment = '-': this.accident.accidentStatusHistory.comment = payload.comment

            if (payload.accidentDepartmentId) this.accident.accidentDepartmentId = payload.accidentDepartmentId
            if (payload.accidentAuthToken) this.accident.accidentAuthToken = payload.accidentAuthToken

            this.$store.dispatch('maintenance/updateAccident', this.accident)
                .then(response => {
                    this.accident = Object.assign({}, response.data)

                    switch (payload.updateType) {
                        case 'user':
                            this.closeAssignUserDialog()
                            break
                        case 'status':
                            this.closeAssignStatusDialog()
                            break
                        case 'department':
                            this.closeAssignDepartmentDialog()
                            break
                        default:
                            break
                    }
                }).catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.assignLoading = false
                })
        },
        setSafeMode() {
            this.actionLoading = true
            const SAFE_MODE_ACCIDENT_TYPE_ID = 2

            const payload = { ...this.accident, accidentTypeId: SAFE_MODE_ACCIDENT_TYPE_ID }

            this.$store.dispatch('maintenance/updateAccident', payload)
                .then((response) => {
                    this.$store.dispatch('showNotification', {
                        type: 'success',
                        message: 'Ustawiono tryb awaryjny.'
                    })
                    this.accident = { ...response.data }
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading = false
                })
        },
        getAccidentStatuses() {
            this.$store.dispatch('maintenance/getAccidentStatuses')
                .catch(error => {
                    errorHandler(error)
                })
        },
        getAccidentDepartments() {
            this.$store.dispatch('maintenance/getAccidentDepartments')
                .catch(error => {
                    errorHandler(error)
                })
        },
        showAssignUserDialog() {
            this.assignUserDialog = true
        },
        showCodeReaderDialog(data) {
            this.codeReaderSettings = data
            this.codeReaderDialog = true
        },
        hideCodeReaderDialog() {
            this.codeReaderDialog = false
        },
        handleQrJSON({ data }) {
            this.updateAccident({
                accidentAuthToken: data.uniqueToken,
                accidentStatusId: 20 // W trakcie usuwania
            })
        },
        startFixAccident() {
            if (!(this.isAdminUser || this.canChangeStatusForDuringRemoval)) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Nie masz uprawnień by wykonać tę czynność.'
                })

                return
            }
            this.updateAccident({
                accidentStatusId: 20 // W trakcie usuwania
            })
        },
        acceptAccident() {
            this.acceptAccidentLoading = true
            const payload = this.accident

            payload.adminAccepted = true
            this.$store.dispatch('maintenance/updateAccident', {...payload})
                .then(() => {
                    this.$store.dispatch('showNotification', {
                        type: 'success',
                        message: 'Awaria została zatwierdzona.'
                    })
                    this.accident.adminAccepted = true
                    this.acceptAccidentLoading = false
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        closeAssignUserDialog() {
            this.assignUserDialog = false
        },
        showAssignStatusDialog() {
            this.assignStatusDialog = true
            this.getAccidentStatuses()
            this.accident.accidentStatusHistory.comment = ''
        },
        closeAssignStatusDialog() {
            this.assignStatusDialog = false
            this.accident.accidentStatusHistory.comment = ''
        },
        showAssignDepartmentDialog() {
            this.assignDepartmentDialog = true
            this.getAccidentDepartments()
        },
        closeAssignDepartmentDialog() {
            this.assignDepartmentDialog = false
        },
        showDeclaredFixTimeDialog() {
            this.declaredFixTimeDialog = true
        },
        saveDeclaredFixTime(payload) {
            this.declaredFixTimeLoading = true

            this.$store.dispatch('maintenance/updateAccident', {...payload})
                .then(response => {
                    this.accident = Object.assign({}, response.data)
                    this.declaredFixTimeLoading = false
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeDeclaredFixTimeDialog()
                })
        },
        closeDeclaredFixTimeDialog() {
            this.declaredFixTimeDialog = false
        },
        addComment() {
            this.actionLoading = true

            this.$store.dispatch('maintenance/createAccidentComment', {
                comment: this.newComment,
                accidentId: this.accident.id,
                createdById: this.userId,
            })
                .then(response => {
                    this.accident.comment.push(response.data)
                    this.newComment = ''
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading = false
                })
        },
        close() {
            this.$router.push({ name: 'accidents' })
                .catch(error => {
                    errorHandler(error)
                })
        }

    }
}
</script>
